import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, FileText, CheckCircle, AlertCircle, Clock, 
  Upload, RefreshCw, Zap, X, File
} from 'lucide-react';
import { multiAmcGetStats, multiAmcGetAmcs, multiAmcExtract } from '../api';
import './MultiAMCDashboard.css';

const MultiAMCDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  const [amcs, setAmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAmc, setSelectedAmc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, amcsRes] = await Promise.all([
        multiAmcGetStats(),
        multiAmcGetAmcs('enabled')
      ]);
      
      if (statsRes.success) {
        setStats(statsRes.statistics || { total: 0, completed: 0, processing: 0, failed: 0 });
      }
      if (amcsRes.success) {
        setAmcs(amcsRes.amcs || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/tiff', 'image/tif', 'image/jpeg', 'image/png'];
      if (validTypes.some(type => file.type.includes(type.split('/')[1]))) {
        setSelectedFile(file);
        setUploadResult(null);
      } else {
        alert('Please select a PDF, TIFF, or image file');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedAmc) {
      alert('Please select a file and AMC');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const result = await multiAmcExtract(selectedFile, selectedAmc);
      setUploadResult(result);
      
      if (result.success) {
        loadDashboardData(); // Refresh stats
      }
    } catch (err) {
      setUploadResult({ success: false, error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setSelectedAmc('');
    setUploadResult(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      enabled: '#22c55e',
      beta: '#eab308',
      disabled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  return (
    <div className="multi-amc-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>
            <Building2 size={32} />
            Multi-AMC Extraction
          </h1>
          <p>Extract data from Mutual Fund application forms across multiple AMCs</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowUploadModal(true)}>
            <Upload size={20} />
            Upload & Extract
          </button>
          <button className="btn-secondary" onClick={loadDashboardData}>
            <RefreshCw size={20} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Extractions</span>
          </div>
        </div>
        <div className="stat-card completed">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card processing">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.processing}</span>
            <span className="stat-label">Processing</span>
          </div>
        </div>
        <div className="stat-card failed">
          <div className="stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.failed}</span>
            <span className="stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* AMC Cards */}
      <div className="section">
        <h2>Supported AMCs</h2>
        <div className="amc-grid">
          {loading ? (
            <div className="loading-state">
              <RefreshCw size={24} className="spin" />
              Loading AMCs...
            </div>
          ) : amcs.length === 0 ? (
            <div className="empty-state">No AMCs configured</div>
          ) : (
            amcs.map(amc => (
              <div key={amc.code} className="amc-card">
                <div className="amc-header">
                  <Building2 size={20} />
                  <span className="amc-code">{amc.code}</span>
                  <span 
                    className="amc-status" 
                    style={{ backgroundColor: getStatusColor(amc.status) }}
                  >
                    {amc.status}
                  </span>
                </div>
                <h3 className="amc-name">{amc.name}</h3>
                <div className="amc-forms">
                  {amc.supported_forms?.slice(0, 3).map(form => (
                    <span key={form} className="form-badge">{form.replace('_', ' ')}</span>
                  ))}
                  {amc.supported_forms?.length > 3 && (
                    <span className="form-badge more">+{amc.supported_forms.length - 3}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <button className="action-card" onClick={() => navigate('/multi-amc/extract')}>
            <Zap size={32} />
            <span>New Extraction</span>
            <small>Upload and extract form data</small>
          </button>
          <button className="action-card" onClick={() => navigate('/multi-amc/work-orders')}>
            <FileText size={32} />
            <span>View Work Orders</span>
            <small>Browse extraction history</small>
          </button>
          <button className="action-card" onClick={() => navigate('/multi-amc/amcs')}>
            <Building2 size={32} />
            <span>AMC Details</span>
            <small>View AMC configurations</small>
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="upload-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Upload size={24} />
                Extract Form Data
              </h3>
              <button className="close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* AMC Selection */}
              <div className="form-group">
                <label>Select AMC *</label>
                <select 
                  value={selectedAmc} 
                  onChange={e => setSelectedAmc(e.target.value)}
                  disabled={uploading}
                >
                  <option value="">-- Select AMC --</option>
                  {amcs.map(amc => (
                    <option key={amc.code} value={amc.code}>
                      {amc.name} ({amc.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div className="form-group">
                <label>Upload File *</label>
                <div 
                  className="drop-zone"
                  onClick={() => !uploading && fileInputRef.current?.click()}
                >
                  {selectedFile ? (
                    <div className="selected-file">
                      <File size={24} />
                      <span>{selectedFile.name}</span>
                      <small>({(selectedFile.size / 1024).toFixed(1)} KB)</small>
                    </div>
                  ) : (
                    <>
                      <Upload size={40} />
                      <p>Click to select or drag & drop</p>
                      <small>PDF, TIFF, JPG, PNG</small>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
                  {uploadResult.success ? (
                    <>
                      <CheckCircle size={20} />
                      <div>
                        <strong>Extraction Complete!</strong>
                        <p>Form Type: {uploadResult.form_name || uploadResult.form_type}</p>
                        <p>Confidence: {((uploadResult.confidence || 0) * 100).toFixed(1)}%</p>
                        <button 
                          className="view-result-btn"
                          onClick={() => navigate(uploadResult.work_order_id 
                            ? `/multi-amc/work-orders/${uploadResult.work_order_id}` 
                            : `/multi-amc/work-orders`)}
                        >
                          View Result
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={20} />
                      <div>
                        <strong>Extraction Failed</strong>
                        <p>{uploadResult.error}</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button 
                className="btn-extract"
                onClick={handleUpload}
                disabled={!selectedFile || !selectedAmc || uploading}
              >
                {uploading ? (
                  <>
                    <RefreshCw size={18} className="spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    Extract
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiAMCDashboard;
